/* eslint-disable import/prefer-default-export */
import { defineStore } from 'pinia';

import useLocalStorage from '@/composables/storage/useLocalStorage.js';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';

import useIdentityQueueEndpoints from '@/composables/api/useIdentityQueueEndpoints';
import useIdentityQueueHelpers from '@/composables/store/identity/useIdentityQueueHelpers';

const { getPendingIdentityDocuments } = useIdentityQueueEndpoints();
const { checkLocalStorage, bindLocalStorage } = useLocalStorage();
const { organizeRoutes, sortRoutes, toClearRouteByException } = useIdentityQueueHelpers;

export const useIdentityQueueStore = defineStore({
  id: 'identityQueue',
  state: () => ({
    identityRoutes: checkLocalStorage('identityRoutes', []),
    queueCounter: checkLocalStorage('queueCounter', 1),
    totalSteps: checkLocalStorage('totalSteps', 1),
    sumsubRecollection: checkLocalStorage('sumsubRecollection', false),
    hasSubmittedToSumsub: false,
  }),
  actions: {
    async fetchIdentityDocuments(fromPath) {
      const { data } = await getPendingIdentityDocuments();
      const overrideRoutes = [];

      return new Promise((res, rej) => {
        if (!data) {
          return;
        }
        Object.entries(data).forEach(([key, value], index) => {
          organizeRoutes(
            this,
            key,
            value,
            overrideRoutes,
            index,
          );
        });

        // 1st condition: from non identity pages, we can call the queue and it should override the queue array
        // 2nd condition: from sumsub pages we can have the queue overridden by sumsub recollection requirement
        // 3rd condition: we have payment based exceptions like dlocal which can trigger the queue again to show a dlocal based page

        if (
          (fromPath && !fromPath.includes('identity'))
          || this.sumsubRecollection
          || toClearRouteByException(this.identityRoutes)
        ) {
          this.identityRoutes = overrideRoutes;
          this.resetCounter();
        }

        this.identityRoutes = sortRoutes(this.identityRoutes);
        this.calculateTotalSteps();
        bindLocalStorage('identityRoutes', this.identityRoutes);
        res();
      });
    },
    calculateTotalSteps() {
      const numberOfBanxaSteps = this.identityRoutes.reduce((acc, identity) => {
        if (identity.provider.toUpperCase() === 'BANXA') {
          acc += 1;
        }
        return acc;
      }, 0);

      // we're count sumsub items as 1 as all documents are collected in one page
      const numberOfSumsubSteps = this.identityRoutes.some(
        (identity) => identity.provider.toUpperCase() === 'SUMSUB',
      )
        ? 1
        : 0;

      const totalSteps = numberOfBanxaSteps + numberOfSumsubSteps;

      if (totalSteps === 0) {
        this.totalSteps = 1;
        return;
      }

      this.totalSteps = numberOfBanxaSteps + numberOfSumsubSteps;
      bindLocalStorage('totalSteps', this.totalSteps);
    },
    updateToNextRoute(route = null) {
      // There might be occucation where we receive two payloads from
      // sumsub. In that case, for the second payload, we have to make
      // sure we don't update the queue counter.
      if (this.hasSubmittedToSumsub && route?.name.includes('sumsub')) {
        return;
      }
      this.queueCounter += 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    updateToPreviousRoute() {
      this.queueCounter -= 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetCounter() {
      this.queueCounter = 1;
      bindLocalStorage('queueCounter', this.queueCounter);
    },
    resetSteps() {
      this.totalSteps = 1;
      bindLocalStorage('totalSteps', this.totalSteps);
    },
    async fetchNextIdentityRoute(route) {
      const orderStore = useOrderFormStore();
      const defaultPaymentRoute = `/payment/${orderStore.getActivePaymentByMode}`;

      return this.fetchIdentityDocuments(route?.path).then(() => {
        // on sumsub recollection we route straight to /status on SumsubForm
        if (route?.name.includes('sumsub') && this.sumsubRecollection) {
          return false;
        }

        if (
          this.identityRoutes.length === 0
          || (orderStore.getActivePaymentByMode
            && this.queueCounter > this.totalSteps)
        ) {
          return defaultPaymentRoute;
        }

        return this.identityRoutes[this.queueCounter - 1].url;
      });
    },
    toggleSumsubRecollection(isRequired) {
      this.sumsubRecollection = isRequired;
      bindLocalStorage('sumsubRecollection', this.sumsubRecollection);
    },
  },
});
